import { WatchfaceService } from '@/services/watchface';
import { testAccess } from './fs/fs-extra.utils';
import { useWatchfaceDevices, WatchfaceDeviceType } from '@/hooks';
import semver from 'semver';

/**
 * return true if path is widget path
 * @param path widget path
 */
export const validWatchfaceDir = async (
  path: string,
  options?: {
    ignoreDeviceType: boolean;
  }
): Promise<boolean> => {
  const service = new WatchfaceService(path);
  const { wholeDevices } = useWatchfaceDevices();

  const ensureDirs = [path, service.descriptionXmlPath ?? ''];
  for (const dir of ensureDirs) {
    if (!(await testAccess(dir))) {
      console.log(`WatchfacePath无效目录：目录 ${dir} 不存在`);
      throw `非表盘文件，无法打开！`;
    }
  }
  const { ignoreDeviceType = false } = options ?? {};
  if (!ignoreDeviceType) {
    // 验证设备是否支持
    const desc = await service.description.getDescriptionData();
    const deviceType = desc?.deviceType;
    const canUse =
      deviceType &&
      wholeDevices.value.some(
        (item) =>
          item.deviceType == deviceType ||
          item.deviceTypeAlias?.includes(deviceType)
      );
    if (!canUse) throw `不支持该设备`;
  }
  return true;
};

export const validWatchfaceName = (
  name: string,
  deviceType: WatchfaceDeviceType | undefined
) => {
  /**
   * 验证表盘名称字数，验证失败时返回错误信息
   * @param name 表盘名称
   * @returns
   */
  const validLength = (name: string) => {
    let chineseCharNum = 0,
      otherCharNum = 0;
    for (let i = 0; i < name.length; i++) {
      const c = name.charAt(i);
      //基本汉字
      if (c.match(/[\u4e00-\u9fa5]/)) {
        chineseCharNum++;
      }
      //基本汉字补充
      else if (c.match(/[\u9FA6-\u9fcb]/)) {
        chineseCharNum++;
      } else {
        otherCharNum++;
      }
    }
    const totalCharNum = chineseCharNum * 3 + otherCharNum;
    if (totalCharNum > 40) {
      return '最多40个字符,汉字不超过13个';
    } else {
      return true;
    }
  };
  name = name?.trim();
  if (!name) return '名称不可为空';

  if (!/^[^.].*/.test(name ?? '')) {
    return '名称不能以 . 开头';
  } else if (!/^[^\\/:*?"<>|]*$/.test(name)) {
    return '名称不能有\\/:*?"<>|字符';
  } else if (/^\d/.test(name) && deviceType == 'O61') {
    return '名称不能以数字开头';
  }
  return validLength(name);
};

export const validWatchfaceVersion = (version: string) => {
  version = version?.trim();
  if (!version) return '版本号不可为空';
  const _version = semver.valid(version);
  if (!_version) return '格式必须为n.n.n，例如：0.0.1';
};

/**
 * 获取字符串的显示(字符)长度，产品规则：1全角字符为1个字符长度
 * @param str
 * @returns
 */
const getStrDisplayLength = (str: string) => {
  let length = 0;
  for (const char of str) {
    const code = char.codePointAt(0);
    // 中日韩文字、全角符号等占 2 个宽度
    if (
      code &&
      ((code >= 0x1100 && code <= 0x11ff) || // Hangul Jamo
        (code >= 0x2e80 && code <= 0xa4cf) || // CJK Radicals Supplement, Kangxi Radicals, Ideographic Description Characters, CJK Symbols and Punctuation, Hiragana, Katakana, Bopomofo, Hangul Compatibility Jamo, Kanbun, Bopomofo Extended, CJK Strokes, Enclosed CJK Letters and Months, CJK Compatibility, CJK Unified Ideographs Extension A
        (code >= 0xac00 && code <= 0xd7af) || // Hangul Syllables
        (code >= 0xf900 && code <= 0xfaff) || // CJK Compatibility Ideographs
        (code >= 0xff00 && code <= 0xffef)) // Halfwidth and Fullwidth Forms
    ) {
      length += 1;
    } else {
      length += 0.5;
    }
  }
  return length;
};

/**
 * 验证表盘信息长度，验证失败时返回错误信息
 * @param str
 * @param max
 * @returns
 */
export const validWatchfaceInfoLength = (str: string, max: number) => {
  const length = getStrDisplayLength(str);
  if (length > max) {
    return `字数不得超过${max}个字符`;
  } else {
    return true;
  }
};
